/**
 * Ronde 4 W8 — Capaciteitsplanning (spec ronde-4/08).
 *
 * Gedeelde DTO's + enum-constanten voor de capaciteits-engine. Backend
 * (capacity-engine.ts) levert deze vorm; frontend (planbord/dashboard,
 * later toegevoegd in parts 2/3) consumeert ze zodat dezelfde cijfers
 * overal getoond worden.
 *
 * Eénheden: uren als `number` (afgerond op 2 decimalen aan de servicelaag,
 * intern op Decimal-precisie gerekend). Geldbedragen (uurkosten/leegloop)
 * eveneens `number` in het DTO, maar de engine rekent ze op Prisma `Decimal`
 * en rondt pas bij serialisatie (CLAUDE.md geld-regel).
 */

import { z } from 'zod'

// ─── Enums ───────────────────────────────────────────────────────────────────

/**
 * Kleur/toon van een medewerker op het planbord en in matching-resultaten.
 * - beschikbaar       — ruim restcapaciteit (groen)
 * - bijna-vol         — bezetting 90-110% (oranje)
 * - vol               — bezetting > 110% / geen rest meer (rood)
 * - niet-beschikbaar  — 0 beschikbare uren in de periode (grijs; volledig
 *                       verlof/verzuim/feestdag of 0-contract)
 * - onderbenut        — bezetting < 60% met > 16u rest (blauw)
 */
export const CAPACITY_TONE = [
  'beschikbaar',
  'bijna-vol',
  'vol',
  'niet-beschikbaar',
  'onderbenut',
] as const
export type CapacityTone = (typeof CAPACITY_TONE)[number]

export const CAPACITY_SIGNAL_TYPE = [
  'OVERBELAST',
  'BIJNA_VOL',
  'ONDERBENUT',
  'STRUCTUREEL_OVERWERK',
  'HOOG_VERZUIM',
  'KOSTEN_LEEGLOOP',
] as const
export type CapacitySignalType = (typeof CAPACITY_SIGNAL_TYPE)[number]

/** Ernst van een capaciteits-signaal — mapt op de notificatie-categorie-laag. */
export const CAPACITY_SIGNAL_ERNST = ['error', 'warning', 'info'] as const
export type CapacitySignalErnst = (typeof CAPACITY_SIGNAL_ERNST)[number]

// ─── Request schemas ─────────────────────────────────────────────────────────

export const capacityDetailQuerySchema = z.object({
  start: z.string().datetime(),
  end: z.string().datetime(),
  entityId: z.string().optional(),
  sortBy: z.enum(['bezetting', 'naam', 'kosten']).default('bezetting'),
})
export type CapacityDetailQuery = z.infer<typeof capacityDetailQuerySchema>

export const employeeCapacityQuerySchema = z.object({
  start: z.string().datetime(),
  end: z.string().datetime(),
  granularity: z.enum(['dag', 'week', 'maand']).default('week'),
})
export type EmployeeCapacityQuery = z.infer<typeof employeeCapacityQuerySchema>

export const matchWithCapacityBodySchema = z.object({
  skillIds: z.array(z.string()).default([]),
  targetDate: z.string().datetime(),
  targetHours: z.number().positive().optional(),
  includeCost: z.boolean().default(false),
  entityId: z.string().optional(),
})
export type MatchWithCapacityBody = z.infer<typeof matchWithCapacityBodySchema>

// ─── Response DTO's ──────────────────────────────────────────────────────────

/**
 * Capaciteit van één medewerker over een periode. Velden volgen de formule in
 * spec §1.1: beschikbaar = contract − verlof − verzuim − feestdag − opleiding;
 * rest = beschikbaar − gepland; bezetting = gepland / beschikbaar × 100.
 *
 * `uurkosten` / `leegloopKosten` zijn `null` wanneer `grossMonthlySalary`
 * onbekend is — kosten worden NOOIT gegokt (CLAUDE.md no-hidden-defaults).
 */
export interface EmployeeCapacityDTO {
  employeeId: string
  naam: string
  contractUrenPerWeek: number
  /** Effectieve werkbare uren per week (incl. ATV) — basis voor daguren. */
  werkUrenPerWeek: number
  /** Werkdagen per week (deler voor daguren; parttime-rooster-verlof-verzuim). */
  werkdagenPerWeek: number

  // Uren in periode
  contractUren: number
  verlofUren: number
  verzuimUren: number
  feestdagUren: number
  /**
   * Batch-48 — uren die wegvallen door een gekozen vrije verjaardag in de
   * periode. Apart van `verlofUren` (kost geen verlofsaldo) en van
   * `feestdagUren` (is geen bedrijfsfeestdag), zodat een uren-breakdown de
   * juiste reden noemt.
   *
   * OPTIONEEL, bewust: de engine vult 'm altijd, maar bestaande literals van
   * dit DTO (snapshots, fixtures, signaal-tests) hoeven er niet voor te wijzigen
   * - een ontbrekende waarde leest als 0.
   */
  verjaardagUren?: number
  opleidingUren: number
  beschikbareUren: number
  geplandeUren: number
  restCapaciteit: number

  // Percentages
  bezettingsgraad: number
  verzuimPercentage: number

  // Signalen
  isOverbelast: boolean
  isOnderbenut: boolean
  tone: CapacityTone

  // Kostprijs (null indien grossMonthlySalary onbekend)
  uurkosten: number | null
  leegloopKosten: number | null
}

export interface TeamCapacityTotalenDTO {
  aantalMedewerkers: number
  contractUren: number
  beschikbareUren: number
  geplandeUren: number
  verlofUren: number
  verzuimUren: number
  feestdagUren: number
  /** Batch-48 — som van `EmployeeCapacityDTO.verjaardagUren`; optioneel om dezelfde reden. */
  verjaardagUren?: number
  opleidingUren: number
  restCapaciteit: number
  bezettingsgraad: number
  verzuimPercentage: number
  leegloopKosten: number | null
}

export interface TeamCapacityDTO {
  periode: { start: string; end: string; label: string }
  totalen: TeamCapacityTotalenDTO
  medewerkers: EmployeeCapacityDTO[]
  signalen: CapacitySignaalDTO[]
}

export interface CapacitySignaalDTO {
  type: CapacitySignalType
  ernst: CapacitySignalErnst
  bericht: string
  medewerkerIds: string[]
}

/**
 * Eén week in de capaciteits-forecast. Backward-compatibele superset van de
 * legacy `CapaciteitWeek` (rapportage-queries.ts): de bestaande velden blijven,
 * `verzuimUren`/`feestdagUren`/`opleidingUren`/`restCapaciteit` zijn toegevoegd.
 */
export interface CapacityForecastWeekDTO {
  week: string
  beschikbareUren: number
  geplandeUren: number
  verlofUren: number
  verzuimUren: number
  feestdagUren: number
  opleidingUren: number
  restCapaciteit: number
  bezettingsgraad: number
}

export interface EnhancedCapacityForecastDTO {
  komendeWeken: CapacityForecastWeekDTO[]
  beschikbareMedewerkers: number
  openWerkbonnen: number
}

/**
 * Eén bucket (dag/week/maand) in de per-medewerker capaciteits-tijdlijn
 * (spec §6.1). Bevat de uren-breakdown voor die deelperiode + de werkbonnen die
 * er ingepland staan, zodat de detailpagina per periode kan tonen waar de
 * geplande uren vandaan komen.
 */
export interface EmployeeCapacityPeriodDTO {
  /** Leesbaar label: "ma 20 mei" (dag), "W21" (week) of "mei 2026" (maand). */
  label: string
  start: string
  end: string
  contractUren: number
  verlofUren: number
  verzuimUren: number
  feestdagUren: number
  /** Batch-48 — vrije verjaardag in deze bucket; optioneel, zie `EmployeeCapacityDTO`. */
  verjaardagUren?: number
  beschikbareUren: number
  geplandeUren: number
  restCapaciteit: number
  bezettingsgraad: number
  werkbonnen: Array<{
    id: string
    werkbonNumber: string
    clientNaam: string
    uren: number
  }>
}

/**
 * Volledige per-medewerker capaciteits-detail (spec §6.1) — het totaal over de
 * hele periode plus een tijdlijn van buckets volgens de gekozen granulariteit.
 */
export interface EmployeeCapacityDetailDTO {
  employee: { id: string; naam: string; contractUren: number }
  periode: { start: string; end: string }
  granularity: EmployeeCapacityQuery['granularity']
  totaal: EmployeeCapacityDTO
  periodes: EmployeeCapacityPeriodDTO[]
}

/**
 * Resultaat van de capaciteit-bewuste medewerker-matching (spec §3). De
 * scoring/ranking-implementatie zit in deel 2 (employee-matching); dit DTO is
 * hier alvast gedefinieerd zodat de types-package één bron is.
 */
export interface EnhancedMatchResultDTO {
  employeeId: string
  naam: string
  score: number
  matchedSkills: Array<{ id: string; name: string; level: number | null }>
  missingSkills: Array<{ id: string; name: string; reason: 'NOT_PRESENT' | 'BELOW_LEVEL' }>
  restCapaciteitOpDag: number
  weekBezetting: number
  geschatteKosten: number | null
  capacityTone: CapacityTone
  scoreBreakdown: {
    skillScore: number
    capacityScore: number
    costScore: number
    experienceScore: number
    travelScore: number
  }
}
